import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";
import { apiFieldErrors } from "../../lib/companyValidation";
import { platformErrorMessage, validateOwnerContact } from "../../lib/platformValidation";
import PlatformOwnerFields from "./PlatformOwnerFields";

/**
 * Point the pending Owner invitation at a different person.
 *
 * The organization is NOT recreated and its lifecycle state does not move: the
 * old invitation is revoked and a new one issued in the SAME transaction, so the
 * "exactly one pending invitation while pending_activation" invariant is never
 * violated in between. Only possible while the organization is awaiting
 * activation — once an Owner has accepted, ownership transfer is a different
 * (later) capability.
 */
export const PlatformOwnerReplaceDialog = ({
  open,
  organizationId,
  invitation,
  onCancel,
  onReplaced,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState({
    owner_first_name: invitation?.first_name || "",
    owner_last_name: invitation?.last_name || "",
    owner_email: invitation?.invited_email || "",
    owner_phone: invitation?.phone || "",
  });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  if (!open) return null;

  const setField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    setFormError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current || busy) return;
    const found = validateOwnerContact(draft);
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    setErrors({});
    inFlight.current = true;
    setBusy(true);
    try {
      const r = await api.post(
        `/platform-admin/organizations/${organizationId}/owner-invitation/replace`,
        {
          owner_first_name: draft.owner_first_name.trim(),
          owner_last_name: draft.owner_last_name.trim(),
          owner_email: draft.owner_email.trim(),
          owner_phone: draft.owner_phone.trim() || null,
        },
        { skipOrgHeader: true },
      );
      onReplaced(r.data);
    } catch (err) {
      reportError("platform.replaceOwnerInvitation", err);
      const fields = apiFieldErrors(err);
      if (fields) setErrors(fields);
      else setFormError(platformErrorMessage(err, t("common.error_generic")));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal qw-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-replace-title"
        data-testid="platform-replace-dialog"
      >
        <div className="h" id="platform-replace-title" data-testid="platform-replace-dialog-title">
          {t("platform.replace_title")}
        </div>
        <form onSubmit={submit} noValidate>
          <div className="body">
            <p className="qw-help" style={{ marginTop: 0 }} data-testid="platform-replace-lead">
              {t("platform.replace_lead")}
            </p>
            <PlatformOwnerFields
              idPrefix="platform-replace-owner"
              draft={draft}
              errors={errors}
              disabled={busy}
              onChange={setField}
            />
            {formError && (
              <div className="qw-error" data-testid="platform-replace-error">
                {formError}
              </div>
            )}
          </div>
          <div className="footer">
            <button
              type="button"
              className="qw-btn ghost"
              onClick={onCancel}
              disabled={busy}
              data-testid="platform-replace-cancel"
            >
              {t("platform.cancel")}
            </button>
            <button
              type="submit"
              className="qw-btn primary"
              disabled={busy}
              data-testid="platform-replace-submit"
            >
              {busy ? t("platform.sending") : t("platform.replace_submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlatformOwnerReplaceDialog;
